import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, tap } from 'rxjs';

import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { LoaderRingType } from '@design-system/components/atoms/loader-ring/entities/loader-ring.types';
import { DropDownViewCloseAction } from '@ux/components/drop-down-view/entities/drop-down-view.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { isEmpty } from '@app/util/util';

@Component({
  template: '',
  standalone: false
})
export abstract class BaseSearchInputAbstractComponent {

  @Input() showError: boolean;
  @Input() icon: Icon;
  @Input() isDisabled: boolean;

  @Output() inputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();
  @Output() removeAutoCompletePlaces: EventEmitter<void> = new EventEmitter<void>();
  @Output() showSearchInput: EventEmitter<boolean> = new EventEmitter<boolean>();

  public showInput = false;
  public value = '';

  public query = '';

  public readonly loaderRingType = LoaderRingType.Neutral;
  public readonly closeAction = DropDownViewCloseAction.Close;

  protected emitAutocomplete$: Subject<string>;
  protected subscription$ = new Subject<void>();

  constructor(
    protected changeDetectorRef: ChangeDetectorRef
  ) { }

  get text(): string {
    return this.value.trimRight();
  }

  get isError(): boolean {
    return this.showError && !this.showInput;
  }

  get isEmptyValue(): boolean {
    return this.value.length === 0;
  }

  public refresh(): void {
    this.changeDetectorRef.markForCheck();
  }

  public clean(): void {
    this.value = '';
    this.query = '';
    this.refresh();
  }

  public set(value: string): void {
    this.value = value;
    this.refresh();
  }

  public emitValueAndClean(): void {
    if (this.text.length) {
      this.clean(); 
    }
  }

  public toggleInput(): void {
    if (!this.isDisabled) {
      if (!this.showInput) {
        this.showSearchInput.emit(this.showInput);
        this.clean();
      } else {
        this.removeAutoCompletePlaces.emit();
        this.clean();
      }
      this.showInput = !this.showInput;
      if (this.showInput) {
        this.initKeyEvent();
      } else {
        this.emitAutocomplete$?.unsubscribe();
      }
      this.changeDetectorRef.detectChanges();
    }
  }

  public setQuery(value: string): void {
    this.query = value;
    this.emitAutocomplete$?.next(this.query);
  }

  protected initKeyEvent(): void {
    this.emitAutocomplete$ = new Subject<string>;
    this.emitAutocomplete$
      .pipe(
        takeUntil(this.subscription$),
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          if (isEmpty(value)) {
            this.clean();
            this.removeAutoCompletePlaces.emit();
          } else {
            this.inputChanged.emit({ value, isEmpty: false });
            this.value = value;
            this.refresh();
          }
        })
      ).subscribe();
  }

}
